// Static fixtures for the read-only scheduling grid (UI Phase 1). Replace with GraphQL once the
// backend fields exist — see domains/load-balancing/types.ts. Data is keyed by week so selecting
// a week pill swaps both the day columns and the shifts shown.
import type {
  ScheduleDayType,
  ScheduleRoleType,
  ScheduleShiftTypeSectionType,
  ScheduleShiftType,
  ScheduleWeekStatusType,
} from '@load-balancing/types';

export const MOCK_ROLES: ScheduleRoleType[] = ['RN', 'LPN', 'UAP', 'CHG'];

export const MOCK_SHIFT_TYPE_SECTIONS: ScheduleShiftTypeSectionType[] = [
  { key: 'day', label: 'Day', time: '07:00–19:00', icon: 'sun' },
  { key: 'night', label: 'Night', time: '19:00–07:00', icon: 'moon' },
];

export const MOCK_WEEK_STATUSES: ScheduleWeekStatusType[] = [
  { key: 'w1', label: 'May 3 – 9', status: 'complete', pendingCount: 0 },
  {
    key: 'w2',
    label: 'May 10 – 16',
    status: 'pending',
    pendingCount: 3,
    hasCoverageGap: true,
  },
  { key: 'w3', label: 'May 17 – 23', status: 'pending', pendingCount: 1 },
  { key: 'w4', label: 'May 24 – 30', status: 'empty', pendingCount: 0 },
  {
    key: 'w5',
    label: 'May 31 – Jun 6',
    status: 'empty',
    pendingCount: 0,
    hasCoverageGap: true,
  },
  { key: 'w6', label: 'Jun 7 – 13', status: 'empty', pendingCount: 0 },
];

export const MOCK_ROLE_STATUSES: Record<ScheduleRoleType, number> = {
  RN: 2,
  LPN: 1,
  UAP: 0,
  CHG: 0,
};

const WEEKDAY_LABELS = [
  'Sun',
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
] as const;

type WeekDateLabels = readonly [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

// The week's Sunday (ISO 'YYYY-MM-DD'); each day's ISO `date` is this plus its weekday offset, so the
// mock carries real calendar dates (the grid needs them for per-slot term lookups, WORK-6528) while
// keeping the human dateLabels.
function buildDays(
  sundayIso: string,
  dateLabels: WeekDateLabels
): ScheduleDayType[] {
  const sunday = new Date(`${sundayIso}T00:00:00Z`);
  return WEEKDAY_LABELS.map((dayLabel, index) => {
    const date = new Date(sunday);
    date.setUTCDate(sunday.getUTCDate() + index);
    return {
      key: dayLabel.toLowerCase(),
      date: date.toISOString().slice(0, 10),
      dayLabel,
      dateLabel: dateLabels[index],
      weekend: index === 0 || index === 6,
    };
  });
}

export const MOCK_DAYS_BY_WEEK: Record<string, ScheduleDayType[]> = {
  w1: buildDays('2026-05-03', [
    'May 3',
    'May 4',
    'May 5',
    'May 6',
    'May 7',
    'May 8',
    'May 9',
  ]),
  w2: buildDays('2026-05-10', [
    'May 10',
    'May 11',
    'May 12',
    'May 13',
    'May 14',
    'May 15',
    'May 16',
  ]),
  w3: buildDays('2026-05-17', [
    'May 17',
    'May 18',
    'May 19',
    'May 20',
    'May 21',
    'May 22',
    'May 23',
  ]),
  w4: buildDays('2026-05-24', [
    'May 24',
    'May 25',
    'May 26',
    'May 27',
    'May 28',
    'May 29',
    'May 30',
  ]),
  w5: buildDays('2026-05-31', [
    'May 31',
    'Jun 1',
    'Jun 2',
    'Jun 3',
    'Jun 4',
    'Jun 5',
    'Jun 6',
  ]),
  w6: buildDays('2026-06-07', [
    'Jun 7',
    'Jun 8',
    'Jun 9',
    'Jun 10',
    'Jun 11',
    'Jun 12',
    'Jun 13',
  ]),
};

type ShiftSeed = Omit<ScheduleShiftType, 'id'>;

function withIds(weekKey: string, seeds: ShiftSeed[]): ScheduleShiftType[] {
  return seeds.map((seed, index) => ({
    id: `${weekKey}-s${index + 1}`,
    ...seed,
  }));
}

/** Compact open-slot seed — start hour derives from the section. */
function open(
  dayKey: string,
  shiftTypeSection: ScheduleShiftType['shiftTypeSection'],
  role: ScheduleShiftType['role']
): ShiftSeed {
  return {
    dayKey,
    shiftTypeSection,
    role,
    // Mock fixture stands in for the backend: CHG is the charge role here, so the open
    // CHG cards demo charge-eligible drops (the real grid gets this from isChargeRole).
    isChargeRole: role === 'CHG',
    startHour: shiftTypeSection === 'night' ? 19 : 7,
    source: 'open',
  };
}

export const MOCK_SHIFTS_BY_WEEK: Record<string, ScheduleShiftType[]> = {
  w1: withIds('w1', [
    {
      dayKey: 'mon',
      shiftTypeSection: 'day',
      role: 'RN',
      startHour: 7,
      workerName: 'Ana Reyes',
      source: 'pattern',
    },
    {
      dayKey: 'mon',
      shiftTypeSection: 'day',
      role: 'RN',
      startHour: 7,
      workerName: 'Carlos Diaz',
      source: 'self',
    },
    {
      dayKey: 'tue',
      shiftTypeSection: 'day',
      role: 'LPN',
      startHour: 7,
      workerName: 'Dana Pratt',
      source: 'pattern',
    },
    {
      dayKey: 'wed',
      shiftTypeSection: 'night',
      role: 'RN',
      startHour: 19,
      workerName: 'Faye Ng',
      source: 'pattern',
    },
    {
      dayKey: 'sun',
      shiftTypeSection: 'day',
      role: 'UAP',
      startHour: 7,
      workerName: 'Hugo Vance',
      source: 'pattern',
    },
    open('tue', 'day', 'RN'),
    open('thu', 'day', 'LPN'),
    open('fri', 'night', 'RN'),
    open('sat', 'day', 'UAP'),
  ]),
  w2: withIds('w2', [
    {
      dayKey: 'mon',
      shiftTypeSection: 'day',
      role: 'RN',
      startHour: 7,
      workerName: 'Ana Reyes',
      source: 'pattern',
    },
    {
      dayKey: 'mon',
      shiftTypeSection: 'day',
      role: 'RN',
      startHour: 7,
      source: 'open',
    },
    {
      dayKey: 'mon',
      shiftTypeSection: 'day',
      role: 'RN',
      startHour: 7,
      workerName: 'Beth Cole',
      source: 'balancer',
      suggested: true,
    },
    {
      dayKey: 'tue',
      shiftTypeSection: 'day',
      role: 'RN',
      startHour: 7,
      workerName: 'Carlos Diaz',
      source: 'self',
    },
    {
      dayKey: 'tue',
      shiftTypeSection: 'day',
      role: 'LPN',
      startHour: 7,
      workerName: 'Dana Pratt',
      source: 'float',
    },
    {
      dayKey: 'wed',
      shiftTypeSection: 'day',
      role: 'RN',
      startHour: 7,
      workerName: 'Ed Lin',
      source: 'pto',
      fixedCommitment: true,
    },
    {
      dayKey: 'mon',
      shiftTypeSection: 'night',
      role: 'RN',
      startHour: 19,
      workerName: 'Faye Ng',
      source: 'pattern',
    },
    {
      dayKey: 'mon',
      shiftTypeSection: 'night',
      role: 'RN',
      startHour: 19,
      source: 'open',
    },
    {
      dayKey: 'sat',
      shiftTypeSection: 'night',
      role: 'LPN',
      startHour: 19,
      workerName: 'Gia Park',
      source: 'self',
    },
    {
      dayKey: 'sun',
      shiftTypeSection: 'day',
      role: 'UAP',
      startHour: 7,
      workerName: 'Hugo Vance',
      source: 'pattern',
    },
    open('mon', 'day', 'LPN'),
    open('wed', 'day', 'LPN'),
    open('thu', 'day', 'LPN'),
    open('mon', 'day', 'UAP'),
    open('tue', 'day', 'UAP'),
    open('mon', 'day', 'CHG'),
    open('tue', 'day', 'CHG'),
    open('fri', 'day', 'RN'),
    open('tue', 'night', 'RN'),
    open('wed', 'night', 'RN'),
    open('mon', 'night', 'LPN'),
    open('thu', 'night', 'UAP'),
  ]),
  w3: withIds('w3', [
    {
      dayKey: 'mon',
      shiftTypeSection: 'day',
      role: 'RN',
      startHour: 7,
      workerName: 'Ana Reyes',
      source: 'pattern',
    },
    {
      dayKey: 'tue',
      shiftTypeSection: 'day',
      role: 'RN',
      startHour: 7,
      workerName: 'Iris Wu',
      source: 'balancer',
      suggested: true,
    },
    {
      dayKey: 'thu',
      shiftTypeSection: 'night',
      role: 'RN',
      startHour: 19,
      source: 'open',
    },
    {
      dayKey: 'fri',
      shiftTypeSection: 'day',
      role: 'LPN',
      startHour: 7,
      workerName: 'Dana Pratt',
      source: 'self',
    },
    open('mon', 'day', 'LPN'),
    open('wed', 'day', 'RN'),
    open('thu', 'day', 'UAP'),
  ]),
  w4: withIds('w4', []),
  w5: withIds('w5', [
    {
      dayKey: 'mon',
      shiftTypeSection: 'day',
      role: 'RN',
      startHour: 7,
      source: 'open',
    },
    {
      dayKey: 'tue',
      shiftTypeSection: 'day',
      role: 'RN',
      startHour: 7,
      source: 'open',
    },
    {
      dayKey: 'wed',
      shiftTypeSection: 'day',
      role: 'RN',
      startHour: 7,
      source: 'open',
    },
    {
      dayKey: 'tue',
      shiftTypeSection: 'night',
      role: 'RN',
      startHour: 19,
      workerName: 'Faye Ng',
      source: 'pattern',
    },
  ]),
  w6: withIds('w6', [
    {
      dayKey: 'wed',
      shiftTypeSection: 'day',
      role: 'UAP',
      startHour: 7,
      workerName: 'Hugo Vance',
      source: 'pattern',
    },
    {
      dayKey: 'thu',
      shiftTypeSection: 'day',
      role: 'RN',
      startHour: 7,
      workerName: 'Ana Reyes',
      source: 'self',
    },
  ]),
};
